import React, {useEffect} from 'react'
import {Link} from 'react-router-dom'
import {KTCard} from '_metronic/helpers'
import StatusBadge from 'app/components/StatusBadge'
import FormatTimestamp from 'app/components/FormatTimestamp'
import ActionDropdown from '../components/ActionDropdown'
import ExportReviewToExcel from '../components/ExportReviewToExcel'

interface ReviewHeaderProps {
  reviews: any[]
  activeTab: string
  filteredItems: any[]
  isCompleteButtonDisabled: boolean
  handleOpenModal: (
    dueDate: string,
    id: string,
    reviewer: {id: string; name: string; email: string}
  ) => void
  handleComplete: () => void
  handleDelete: (reviewId: string, systemId?: string, isReview?: boolean) => void
  type?: string
}

const ReviewHeader: React.FC<ReviewHeaderProps> = ({
  reviews,
  activeTab,
  filteredItems,
  isCompleteButtonDisabled,
  handleOpenModal,
  handleComplete,
  handleDelete,
  type,
}) => {
  const [userLength, setUserLength] = React.useState(0)

  useEffect(() => {
    let filtered = reviews
      .map((review) => ({
        ...review,
        systems: Object.entries(review.systems || {}).map(([systemId, system]: [string, any]) => ({
          systemId,
          ...system,
        })),
      }))
      .flatMap((review) =>
        review.systems.map((system) => ({
          ...review,
          system,
          deniedUsers: Object.entries(system.users || {})
            .filter(([_, user]: [string, any]) => user.status === 'Denied')
            .map(([userId, user]: [string, any]) => ({
              userId,
              ...user,
            })),
        }))
      )
      .flatMap((item) =>
        item.deniedUsers.map((user) => ({
          ...item,
          user,
        }))
      )
    const length = filtered.length
    setUserLength(length)
  }, [reviews])
  
  return (
    <KTCard className='mb-5 mb-xl-8'>
      {reviews.length > 0 ? (
        reviews.map((review) => (
          <div className='px-9 pt-6' key={review.id}>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <div>
                <Link
                  to='/access/review'
                  className='text-gray-600 fw-semibold mb-3 fs-7 text-hover-dark'
                  style={{cursor: 'pointer'}}
                >
                  Access Reviews
                </Link>
                <div className='d-flex align-items-center mb-2 mt-3'>
                  <h1 className='mb-0 me-2 fw-bold fs-4'>
                    {review.reviewName.charAt(0).toUpperCase() + review.reviewName.slice(1)}
                  </h1>
                  <StatusBadge
                    status={
                      review.status as
                        | 'Active'
                        | 'Inactive'
                        | 'Deactivated'
                        | 'Pending'
                        | 'Due'
                        | 'Completed'
                        | 'Not Started'
                        | 'In review'
                    }
                  />
                </div>

                <div className='d-flex align-items-center text-gray-700 fs-7 gap-3'>
                  <span>
                    Created on: <FormatTimestamp timestamp={review.createdAt} />
                  </span>
                  |{' '}
                  {review.startDate && (
                    <>
                      <span>
                        Started on: <FormatTimestamp timestamp={review.startDate} />
                      </span>{' '}
                      |{' '}
                    </>
                  )}
                  {review.completedAt ? (
                    <span>
                      Completed on: <FormatTimestamp timestamp={review.completedAt} />
                    </span>
                  ) : (
                    <span>
                      Due Date: <FormatTimestamp timestamp={review.dueDate} />
                    </span>
                  )}
                </div>
              </div>
              {type !== 'access-change' && (
                <div className='d-flex align-items-center gap-3'>
                  {review.status === 'Completed' ? (
                    <ExportReviewToExcel
                      filteredItems={filteredItems}
                      reviewName={review.reviewName}
                      disabled={review.status === 'pending'}
                    />
                  ) : review.status === 'Pending' ? (
                    <button
                      className='btn btn-sm fs-6'
                      style={{background: '#0E9F6E', height: '40px', color: '#fff'}}
                      onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                        (e.currentTarget.style.backgroundColor = '#148363')
                      }
                      onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                        (e.currentTarget.style.backgroundColor = '#0E9F6E')
                      }
                      onClick={() => handleOpenModal(review.dueDate, review.id, review.reviewer)}
                    >
                      Start review
                    </button>
                  ) : (
                    <button
                      className='btn btn-sm fs-6'
                      style={{background: '#0E9F6E', height: '40px', color: '#fff'}}
                      onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                        (e.currentTarget.style.backgroundColor = '#148363')
                      }
                      onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                        (e.currentTarget.style.backgroundColor = '#0E9F6E')
                      }
                      onClick={() => handleComplete()}
                      disabled={isCompleteButtonDisabled || reviews[0]?.status === 'Completed'}
                    >
                      Complete
                    </button>
                  )}
                  <ActionDropdown
                    reviewId={review.id}
                    statusHeader={reviews[0]?.status}
                    onEdit={true}
                    onDelete={() => handleDelete(review.id, undefined, true)}
                    isReview={true}
                  />
                </div>
              )}
            </div>
            <div className=''>
              <div className='d-flex h-55px'>
                <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-semibold flex-nowrap'>
                  <li className='nav-item'>
                    <Link
                      to={`/access/review/${review.id}`}
                      className={`nav-link me-6 ${
                        activeTab === 'systems' ? 'text-active-primary active' : ''
                      }`}
                    >
                      Systems
                    </Link>
                  </li>

                  <li className='nav-item'>
                    <Link
                      to={`/access/review/${review.id}/access-changes`}
                      className={`nav-link me-6 ${
                        activeTab === 'access-changes' ? 'text-active-primary active' : ''
                      }`}
                    >
                      Access Changes{' '}
                      {userLength > 0 && (
                        <div
                          className='d-flex justify-content-center align-items-center'
                          style={{
                            background: '#E5E7EB',
                            borderRadius: '9999px',
                            width: '20px',
                            height: '20px',
                            marginInlineStart: '6px',
                          }}
                        >
                          <span className='fw-medium' style={{color: '#1F2A37', fontSize: '12px'}}>
                            {userLength}
                          </span>
                        </div>
                      )}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        ))
      ) : (
        <></>
      )}
    </KTCard>
  )
}

export default ReviewHeader
